/**
 * A term's calendar for the week chips, the Weeks view and the week fields, or null when
 * the term has none.
 *
 * Asked of the list of terms first: `GET /api/terms/:year` answers 404 for a term nobody
 * configured, and a 404 on every page that shows weeks would be noise in the console
 * (and a "problem" in the browser tests). The list is kept for half a minute, so the
 * pages of one visit share it and a calendar an admin just saved shows up soon after.
 */
import type { TermView } from '@studio/shared';
import { api } from './api';

const KEEP_MS = 30_000;
let listing: { at: number; years: Promise<Set<string>> } | null = null;

export async function loadTermView(year: string | null | undefined): Promise<TermView | null> {
  if (!year || !/^\d{4}$/.test(year)) return null;
  if (!listing || Date.now() - listing.at > KEEP_MS) {
    const years = api
      .terms()
      .then((list) => new Set(list.map((t) => t.year)))
      .catch(() => {
        // asked again next time
        listing = null;
        return new Set<string>();
      });
    listing = { at: Date.now(), years };
  }
  if (!(await listing.years).has(year)) return null;
  try {
    return await api.term(year);
  } catch {
    return null;
  }
}
